import { ArrowDownIcon, ArrowUpIcon } from "@phosphor-icons/react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type RowData,
  useReactTable,
} from "@tanstack/react-table";
import { clsx } from "clsx";
import { Fragment, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { Icon, type IconKey } from "./icon";
import { Skeleton } from "./skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

// Lets column definitions carry a server-side sort field name via `meta`,
// e.g. `{ meta: { sortKey: 'invoiceDate' } }`. Present => the header renders
// as a clickable sort control (see `sortKey` handling in `DataTable` below).
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- lint-backlog: binding kept on purpose (prop API, rest-spread exclusion or declaration-merge signature); removing it changes the API
  interface ColumnMeta<TData extends RowData, TValue> {
    sortKey?: string;
  }
}

const DEFAULT_SKELETON_ROW_COUNT = 5;

export interface DataTableEmptyState {
  description?: ReactNode;
  icon?: IconKey;
  title: string;
}

export interface DataTableSortState {
  direction: "asc" | "desc";
  key: string;
}

export interface DataTableProps<TData, TValue = unknown> {
  className?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Falls back to a plain "No results found" message when omitted. */
  emptyState?: DataTableEmptyState;
  /** Rendered above the table — search inputs etc; owned entirely by the caller. */
  filterElement?: ReactNode;
  id?: string;
  /** Caller-computed: true when there's nothing to show and not loading. */
  isEmpty?: boolean;
  isLoading?: boolean;
  /**
   * When provided alongside `renderExpandedRow`, an extra sibling row is
   * rendered directly beneath any row for which this returns true —
   * containing whatever `renderExpandedRow` returns, spanning all columns.
   */
  isRowExpanded?: (row: TData) => boolean;
  /**
   * Pointer convenience: makes the whole row a click target, with a pointer
   * cursor and hover background.
   *
   * It is deliberately NOT a keyboard or assistive-technology affordance. A
   * focusable `<tr>` announces as a plain row and carries no hint that Enter
   * activates it, and `role="button"` cannot fix that — it would override
   * `role="row"` and orphan the cells. Callers that use this MUST also put a
   * real focusable control in a cell (see CognitoLookup's chevron button), so
   * keyboard and screen-reader users get one properly-named target per row.
   */
  onRowClick?: (row: TData) => void;
  /**
   * Fired with the clicked column's `sortKey` — direction toggling is the
   * caller's job, not this component's.
   */
  onSortChange?: (sortKey: string) => void;
  /** Content rendered inside the expanded sibling row — see `isRowExpanded`. */
  renderExpandedRow?: (row: TData) => ReactNode;
  /**
   * Current applied sort (single primary sort), used only to render header
   * direction icons/state. Null/undefined = unsorted.
   */
  sortState?: DataTableSortState | null;
}

const DataTableEmpty = ({
  emptyState,
}: {
  emptyState?: DataTableEmptyState;
}) => (
  <div className="flex w-full flex-col items-center justify-center gap-8 p-10 text-center">
    <Icon
      className="!size-16 text-muted-foreground"
      data-testid="data-table-empty-icon"
      icon={emptyState?.icon ?? "folderOpen"}
    />
    <h3>{emptyState?.title ?? "No results found"}</h3>
    <span className="text-base font-normal">
      {emptyState?.description ?? "We couldn't find what you are looking for."}
    </span>
  </div>
);

export const DataTable = <TData, TValue = unknown>({
  className,
  columns,
  data,
  emptyState,
  filterElement,
  id,
  isEmpty,
  isLoading,
  isRowExpanded,
  onRowClick,
  onSortChange,
  renderExpandedRow,
  sortState,
}: DataTableProps<TData, TValue>) => {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  // Only replace the table with skeleton rows on a first load with nothing
  // painted yet — a page/sort change on an already-populated table should
  // never flash back to skeleton.
  const showSkeleton = isLoading && data.length === 0;

  return (
    <div className={twMerge("flex w-full flex-col gap-5", className)} id={id}>
      {filterElement && (
        <div className="flex w-full items-center gap-3">{filterElement}</div>
      )}

      {isEmpty ? (
        <DataTableEmpty emptyState={emptyState} />
      ) : (
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sortKey = header.column.columnDef.meta?.sortKey;
                  const isAsc =
                    !!sortKey &&
                    sortState?.key === sortKey &&
                    sortState.direction === "asc";
                  const isDesc =
                    !!sortKey &&
                    sortState?.key === sortKey &&
                    sortState.direction === "desc";

                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : sortKey ? (
                        <button
                          className="inline-flex items-center gap-1 font-semibold"
                          onClick={() => onSortChange?.(sortKey)}
                          type="button"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          <Icon
                            className={clsx({
                              "opacity-50": !isAsc && !isDesc,
                            })}
                            icon={
                              isAsc
                                ? ArrowDownIcon
                                : isDesc
                                  ? ArrowUpIcon
                                  : ArrowDownIcon
                            }
                            size="sm"
                          />
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {showSkeleton
              ? Array.from({ length: DEFAULT_SKELETON_ROW_COUNT }).map(
                  (_, rowIndex) => (
                    <TableRow key={`skeleton-row-${rowIndex}`}>
                      {columns.map((_, colIndex) => (
                        <TableCell
                          key={`skeleton-cell-${rowIndex}-${colIndex}`}
                        >
                          <Skeleton className="h-5 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ),
                )
              : table.getRowModel().rows.map((row) => {
                  const expanded =
                    !!isRowExpanded && isRowExpanded(row.original);

                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        className={
                          onRowClick
                            ? "cursor-pointer hover:bg-muted/50"
                            : undefined
                        }
                        // No tabIndex or key handler by design: keyboard access
                        // belongs to the caller's in-cell control, so a row is
                        // not a second, unnamed tab stop for the same action.
                        onClick={
                          onRowClick
                            ? () => onRowClick(row.original)
                            : undefined
                        }
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {expanded && renderExpandedRow && (
                        <TableRow key={`${row.id}-expanded`}>
                          <TableCell colSpan={columns.length}>
                            {renderExpandedRow(row.original)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export type { ColumnDef };
